import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Autocomplete, Badge, Box, Button, Chip, CircularProgress, Divider,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Skeleton,
  Snackbar, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import {
  Add, Check, Close, EditOutlined, DeleteOutline, Refresh,
  TuneRounded, FileDownloadOutlined,
} from '@mui/icons-material';
import { usePermissions } from '../../../../contexts/PermissionContext';
import * as api from '../../../../services/subscriptionAPI';

const RESOLUTIONS = ['1k', '2k', '4k', 'premium'];

/** Global rates for a content type, keyed by resolution (supports nested currency map). */
function globalRatesFor(ct, currency) {
  const g = ct?.global || {};
  const nested = g[currency];
  if (nested && typeof nested === 'object' && !nested.tokens) {
    return nested;
  }
  // Legacy flat shape: { 1k: row } — treat as INR only
  if (currency === 'INR' && (g['1k'] || g['2k'])) {
    return g;
  }
  return {};
}

const RES_LABELS = { '1k': '1K', '2k': '2K', '4k': '4K', 'premium': 'Premium' };

const SCOPE_LABELS = { plan: 'Plan', merchant: 'Merchant' };

const SCOPE_CHIP_SX = {
  plan:     { bgcolor: 'primary.50',   color: 'primary.dark',   borderColor: 'primary.200' },
  merchant: { bgcolor: 'purple.50',    color: '#6b21a8',        borderColor: '#d8b4fe' },
  package:  { bgcolor: 'warning.50',   color: 'warning.dark',   borderColor: 'warning.200' },
};

const NUM_NO_SPIN = {
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]': { MozAppearance: 'textfield' },
};

const TH = {
  fontWeight: 700, fontSize: 11, color: 'text.secondary',
  letterSpacing: 0.5, textTransform: 'uppercase',
  bgcolor: 'grey.50', py: 1.5,
};

// ── inline-editable token cell ─────────────────────────────────────────────────

function TokenCell({ row, canEdit, onSave, onCreate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState('');
  const [saving, setSaving]   = useState(false);

  const startEdit   = () => { setVal(String(row.tokens)); setEditing(true); };
  const startCreate = () => { setVal(''); setEditing(true); };
  const cancel      = () => setEditing(false);

  const save = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) return;
    setSaving(true);
    try {
      if (row) await onSave(row.id, n);
      else await onCreate(n);
      setEditing(false);
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <TableCell align="center" sx={{ px: 1, py: 0.75 }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <TextField size="small" type="number" value={val} autoFocus
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
            inputProps={{ min: 0, style: { width: 60, textAlign: 'center', padding: '4px 6px', fontWeight: 700 } }}
            sx={{ ...NUM_NO_SPIN }} />
          <IconButton size="small" onClick={save} disabled={saving} color="primary"
            sx={{ width: 24, height: 24 }}>
            {saving ? <CircularProgress size={12} /> : <Check sx={{ fontSize: 14 }} />}
          </IconButton>
          <IconButton size="small" onClick={cancel} sx={{ width: 24, height: 24 }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </TableCell>
    );
  }

  if (!row) {
    if (canEdit && onCreate) {
      return (
        <TableCell align="center" sx={{ py: 1 }}>
          <Tooltip title="Click to set price" placement="top" arrow>
            <Box display="inline-flex" alignItems="center" gap={0.3}
              onClick={startCreate}
              sx={{ cursor: 'pointer', color: 'text.disabled', borderRadius: 1.5, px: 1, py: 0.5,
                transition: 'color 0.15s, background 0.15s',
                '&:hover': { color: 'primary.main', bgcolor: 'primary.50' } }}>
              <Add sx={{ fontSize: 13 }} />
              <Typography variant="caption" fontSize={11} fontWeight={600}>Set</Typography>
            </Box>
          </Tooltip>
        </TableCell>
      );
    }
    return (
      <TableCell align="center" sx={{ color: 'text.disabled' }}>
        <Typography variant="caption" sx={{ fontStyle: 'italic', fontSize: 12 }}>—</Typography>
      </TableCell>
    );
  }

  return (
    <TableCell align="center" sx={{ py: 1 }}>
      <Tooltip title={canEdit ? 'Click to edit' : ''} placement="top" arrow>
        <Box display="inline-flex" alignItems="center" gap={0.5}
          onClick={canEdit ? startEdit : undefined}
          sx={{
            cursor: canEdit ? 'pointer' : 'default',
            borderRadius: 1.5, px: 1.5, py: 0.5,
            transition: 'background 0.15s',
            '&:hover': canEdit ? { bgcolor: 'primary.50' } : {},
            '&:hover .edit-hint': { opacity: 1 },
          }}>
          <Typography fontWeight={700} fontSize={14} lineHeight={1}>{row.tokens}</Typography>
          {canEdit && (
            <EditOutlined className="edit-hint"
              sx={{ fontSize: 11, color: 'primary.main', opacity: 0, transition: '0.15s' }} />
          )}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

// ── main ──────────────────────────────────────────────────────────────────────

export default function ContentPricingManagement() {
  const { hasPermission } = usePermissions();
  const canView           = hasPermission('Content Pricing', 'view');
  const canUpdate         = hasPermission('Content Pricing', 'update');
  const canOverrideCreate = hasPermission('Content Pricing', 'override_create');
  const canOverrideDelete = hasPermission('Content Pricing', 'override_delete');

  const [matrix, setMatrix]   = useState(null);
  const [globalCurrency, setGlobalCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);
  const [snack, setSnack]     = useState({ open: false, msg: '', sev: 'success' });
  const show = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── override panel ─────────────────────────────────────────────────────────
  const [overrideCt, setOverrideCt]         = useState(null);
  const [ovForm, setOvForm]                 = useState({ scope_type: 'plan', scope_target_id: '', resolution: '1k', tokens: '' });
  const [ovSaving, setOvSaving]             = useState(false);
  const [ovErrors, setOvErrors]             = useState({});
  const [ovDeleting, setOvDeleting]         = useState(null);
  const [planOptions, setPlanOptions]       = useState([]);
  const [merchantOpts, setMerchOpts]        = useState([]);
  const [merchantSearch, setMerchSearch]    = useState('');
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [ovNames, setOvNames]               = useState({});

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getContentPricing();
      setMatrix(r.data);
      const defaultCurr = r.data.currencies?.find(c => c.is_default)?.code
        || r.data.currencies?.[0]?.code
        || 'INR';
      setGlobalCurrency(prev => prev || defaultCurr);
    } catch { show('Failed to load pricing', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canView) loadMatrix(); }, [canView, loadMatrix]);

  const handleExport = async () => {
    try {
      const res = await api.exportContentPricing();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content_pricing_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { show('Export failed', 'error'); }
  };

  // load plans + resolve plan names when panel opens
  useEffect(() => {
    if (!overrideCt) return;
    api.getPlans({ per_page: 100, status: 1 }).then(r => {
      const plans = r.data.items || [];
      setPlanOptions(plans);
      const names = {};
      overrideCt.overrides.forEach(ov => {
        if (ov.scope_type === 'plan') {
          const p = plans.find(pl => pl.id === ov.scope_target_id);
          if (p) names[ov.id] = p.display_name;
        }
      });
      setOvNames(prev => ({ ...prev, ...names }));
    }).catch(() => {});
  }, [overrideCt]);

  // resolve merchant names for existing overrides
  useEffect(() => {
    if (!overrideCt) return;
    overrideCt.overrides
      .filter(ov => ov.scope_type === 'merchant' && !ovNames[ov.id])
      .forEach(ov => {
        api.searchMerchants(String(ov.scope_target_id))
          .then(r => {
            const u = (r.data.items || []).find(m => m.user_id === ov.scope_target_id);
            if (u) setOvNames(prev => ({ ...prev, [ov.id]: u.display_name || u.email }));
          }).catch(() => {});
      });
  }, [overrideCt]); // eslint-disable-line react-hooks/exhaustive-deps

  // merchant search debounce
  useEffect(() => {
    if (!overrideCt || ovForm.scope_type !== 'merchant' || merchantSearch.length < 2) {
      setMerchOpts([]); return;
    }
    const t = setTimeout(() => {
      api.searchMerchants(merchantSearch).then(r => setMerchOpts(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [merchantSearch, ovForm.scope_type, overrideCt]);

  const saveCell = async (pricingId, tokens) => {
    try {
      const r = await api.updateContentPricing(pricingId, { tokens });
      setMatrix(m => ({
        ...m,
        content_types: m.content_types.map(ct => {
          const u = r.data;
          if (ct.id !== u.content_type_id) return ct;
          const curr = (u.currency_code || 'INR').toUpperCase();
          return {
            ...ct,
            global: {
              ...ct.global,
              [curr]: { ...(ct.global?.[curr] || {}), [u.resolution]: u },
            },
          };
        }),
      }));
      show('Saved');
    } catch (e) { show(e.response?.data?.message || 'Save failed', 'error'); throw e; }
  };

  const createCell = async (ctId, resolution, currency, tokens) => {
    try {
      const r = await api.createContentPricing({ content_type_id: ctId, resolution, currency_code: currency, tokens });
      setMatrix(m => ({
        ...m,
        content_types: m.content_types.map(ct => {
          if (ct.id !== ctId) return ct;
          const curr = (r.data.currency_code || currency).toUpperCase();
          return { ...ct, global: { ...ct.global, [curr]: { ...(ct.global?.[curr] || {}), [r.data.resolution]: r.data } } };
        }),
      }));
      show('Price set');
    } catch (e) { show(e.response?.data?.message || 'Failed to set price', 'error'); throw e; }
  };

  const openPanel = (ct) => {
    setOverrideCt(ct);
    setOvNames({});
    setOvForm({ scope_type: 'plan', scope_target_id: '', resolution: '1k', tokens: '', currency_code: globalCurrency });
    setSelectedTarget(null);
    setMerchSearch('');
    setOvErrors({});
  };

  const saveOverride = async () => {
    const errs = {};
    if (!ovForm.scope_target_id && ovForm.scope_type !== 'global')
      errs.scope_target_id = `Select a ${ovForm.scope_type === 'plan' ? 'plan' : ovForm.scope_type === 'merchant' ? 'merchant' : 'package ID'}`;
    if (ovForm.tokens === '' || ovForm.tokens === null || ovForm.tokens === undefined)
      errs.tokens = 'Token cost is required';
    else if (Number(ovForm.tokens) < 0)
      errs.tokens = 'Cannot be negative';
    if (Object.keys(errs).length > 0) { setOvErrors(errs); return; }
    setOvErrors({});
    setOvSaving(true);
    try {
      const r = await api.createPricingOverride({
        content_type_id: overrideCt.id,
        scope_type:      ovForm.scope_type,
        scope_target_id: Number(ovForm.scope_target_id),
        resolution:      ovForm.resolution,
        tokens:          Number(ovForm.tokens),
        currency_code:   ovForm.currency_code || globalCurrency,
      });
      const updated = [...overrideCt.overrides.filter(o => o.id !== r.data.id), r.data];
      setMatrix(m => ({
        ...m,
        content_types: m.content_types.map(ct =>
          ct.id !== overrideCt.id ? ct : { ...ct, overrides: updated }
        ),
      }));
      setOverrideCt(prev => ({ ...prev, overrides: updated }));
      if (selectedTarget) {
        const name = selectedTarget.display_name || selectedTarget.email || '';
        if (name) setOvNames(prev => ({ ...prev, [r.data.id]: name }));
      }
      setOvForm({ scope_type: 'plan', scope_target_id: '', resolution: '1k', tokens: '', currency_code: globalCurrency });
      setSelectedTarget(null);
      show('Override saved');
    } catch (e) { show(e.response?.data?.message || 'Failed', 'error'); }
    finally { setOvSaving(false); }
  };

  const deleteOverride = async (id) => {
    setOvDeleting(id);
    try {
      await api.deletePricingOverride(id);
      const updated = overrideCt.overrides.filter(o => o.id !== id);
      setMatrix(m => ({
        ...m,
        content_types: m.content_types.map(ct =>
          ct.id !== overrideCt.id ? ct : { ...ct, overrides: updated }
        ),
      }));
      setOverrideCt(prev => ({ ...prev, overrides: updated }));
      show('Override deleted');
    } catch (e) { show(e.response?.data?.message || 'Delete failed', 'error'); }
    finally { setOvDeleting(null); }
  };

  if (!canView) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography color="text.secondary">You don't have permission to view content pricing.</Typography>
    </Box>
  );

  const resolutions = matrix?.resolutions || RESOLUTIONS;

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Header — sticky ───────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>
            Content Pricing
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">
              Token cost per content type &amp; resolution
            </Typography>
            {!loading && matrix && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="text.disabled">
                  {matrix.content_types.length} types ·{' '}
                  {matrix.content_types.reduce((s, ct) => s + ct.overrides.length, 0)} override{matrix.content_types.reduce((s, ct) => s + ct.overrides.length, 0) !== 1 ? 's' : ''}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {canUpdate && (
            <Chip size="small" icon={<EditOutlined sx={{ fontSize: 12 }} />}
              label="Click cell to edit"
              variant="outlined"
              sx={{ fontSize: 11, height: 24, color: 'text.secondary', borderColor: 'divider' }} />
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadMatrix} disabled={loading}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.100' } }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="medium" variant="outlined" startIcon={<FileDownloadOutlined />} onClick={handleExport}
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
            Export CSV
          </Button>
        </Box>
      </Box>


      {/* Global rates by currency (INR / USD) */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, px: 2, pt: 1 }}>
        <Tabs
          value={globalCurrency}
          onChange={(_, v) => setGlobalCurrency(v)}
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
        >
          {(matrix?.currencies || []).map(c => (
            <Tab key={c.code} label={`Global ${c.code}`} value={c.code} />
          ))}
        </Tabs>
      </Paper>

      {/* ── Matrix table ──────────────────────────────────────────────────── */}
      <TableContainer component={Paper} variant="outlined"
        sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                ...TH, minWidth: 210, px: 2,
                position: 'sticky', left: 0, zIndex: 3, bgcolor: 'grey.50',
              }}>
                Content Type
              </TableCell>
              {resolutions.map(r => (
                <TableCell key={r} align="center" sx={{ ...TH, minWidth: 100 }}>
                  {RES_LABELS[r] || r.toUpperCase()}
                  <Typography component="span" sx={{ fontSize: 9, fontWeight: 400,
                    color: 'text.disabled', textTransform: 'lowercase', letterSpacing: 0, ml: 0.4 }}>
                    tok
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ ...TH, minWidth: 100 }}>
                Overrides
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: resolutions.length + 2 }).map((__, j) => (
                      <TableCell key={j} sx={{ py: 1.5 }}><Skeleton height={20} /></TableCell>
                    ))}
                  </TableRow>
                ))
              : (matrix?.content_types || []).map(ct => (
                  <TableRow key={ct.id} sx={{
                    '&:hover': { bgcolor: 'grey.50' },
                    '&:last-child td': { border: 0 },
                  }}>
                    {/* Content type label */}
                    <TableCell sx={{
                      fontWeight: 600, fontSize: 13, px: 2,
                      position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                      borderRight: '1px solid', borderColor: 'divider',
                    }}>
                      {ct.display_name}
                    </TableCell>

                    {/* Token cells */}
                    {resolutions.map(res => (
                      <TokenCell key={res}
                        row={globalRatesFor(ct, globalCurrency)[res] || null}
                        canEdit={canUpdate}
                        onSave={saveCell}
                        onCreate={canUpdate ? (tokens) => createCell(ct.id, res, globalCurrency, tokens) : undefined} />
                    ))}

                    {/* Overrides action cell */}
                    <TableCell align="center" sx={{ px: 1.5 }}>
                      {canOverrideCreate ? (
                        <Tooltip title={ct.overrides.length > 0
                          ? `${ct.overrides.length} active override${ct.overrides.length !== 1 ? 's' : ''} — click to manage`
                          : 'Add plan or merchant override'} arrow>
                          <Badge badgeContent={ct.overrides.length || null}
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14, top: 3, right: 3 } }}>
                            <Button size="small" variant={ct.overrides.length > 0 ? 'contained' : 'outlined'}
                              onClick={() => openPanel(ct)}
                              startIcon={ct.overrides.length > 0 ? <TuneRounded sx={{ fontSize: 13 }} /> : <Add sx={{ fontSize: 13 }} />}
                              sx={{
                                fontSize: 11, fontWeight: 600, textTransform: 'none',
                                borderRadius: 1.5, py: 0.4, px: 1.2,
                                minWidth: 0,
                                ...(ct.overrides.length === 0 && { color: 'text.secondary', borderColor: 'divider' }),
                              }}>
                              {ct.overrides.length > 0 ? 'Manage' : 'Add'}
                            </Button>
                          </Badge>
                        </Tooltip>
                      ) : ct.overrides.length > 0 ? (
                        <Chip size="small" label={ct.overrides.length}
                          onClick={() => openPanel(ct)}
                          sx={{ fontWeight: 700, fontSize: 11, height: 20, cursor: 'pointer' }} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Override Dialog ───────────────────────────────────────────────── */}
      <Dialog open={!!overrideCt} onClose={() => setOverrideCt(null)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>

        <DialogTitle sx={{ pb: 1.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography fontWeight={800} fontSize={17} lineHeight={1.2}>
                {overrideCt?.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={0.3} display="block">
                Override global rates for specific plans or merchants
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOverrideCt(null)}
              sx={{ color: 'text.secondary' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>

          {/* Global rates reference */}
          {overrideCt && Object.keys(globalRatesFor(overrideCt, globalCurrency)).length > 0 && (
            <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
              borderRadius: 2, px: 2, py: 1.5, mb: 3 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                Current global rates ({globalCurrency})
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {resolutions.filter(r => globalRatesFor(overrideCt, globalCurrency)[r]).map(r => (
                  <Box key={r} textAlign="center">
                    <Typography variant="caption" color="text.secondary" display="block">
                      {RES_LABELS[r]}
                    </Typography>
                    <Typography fontWeight={700} fontSize={15} color="text.primary">
                      {globalRatesFor(overrideCt, globalCurrency)[r].tokens}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" fontSize={9}>tokens</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Existing overrides */}
          {overrideCt?.overrides?.length > 0 && (
            <Box mb={3}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                letterSpacing={0.5} textTransform="uppercase" mb={1.5} display="block">
                Active overrides ({overrideCt.overrides.length})
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {overrideCt.overrides.map(ov => {
                  const ovCurrency = (ov.currency_code || 'INR').toUpperCase();
                  const globalTok = globalRatesFor(overrideCt, ovCurrency)?.[ov.resolution]?.tokens;
                  const cheaper   = globalTok != null && ov.tokens < globalTok;
                  const costlier  = globalTok != null && ov.tokens > globalTok;
                  const chipSx    = SCOPE_CHIP_SX[ov.scope_type] || {};
                  return (
                    <Box key={ov.id} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      border: '1px solid', borderColor: 'divider', borderRadius: 2,
                      px: 2, py: 1.25,
                      '&:hover': { borderColor: 'primary.light', bgcolor: 'grey.50' },
                    }}>
                      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Chip size="small"
                          label={SCOPE_LABELS[ov.scope_type] || ov.scope_type}
                          sx={{ fontWeight: 700, fontSize: 10, height: 18,
                            border: '1px solid', ...chipSx,
                            '& .MuiChip-label': { px: 0.8 } }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                            {ovNames[ov.id] || `ID ${ov.scope_target_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {RES_LABELS[ov.resolution] || ov.resolution?.toUpperCase()} resolution
                          </Typography>
                        </Box>
                        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                          <Typography fontWeight={800} fontSize={15}
                            color={cheaper ? 'success.main' : costlier ? 'warning.main' : 'text.primary'}>
                            {ov.tokens}
                            <Typography component="span" variant="caption"
                              color="text.disabled" fontWeight={400} ml={0.4}>tokens</Typography>
                          </Typography>
                          {globalTok != null && (
                            <Typography variant="caption" display="block"
                              color={cheaper ? 'success.light' : costlier ? 'warning.light' : 'text.disabled'}>
                              {cheaper ? '↓' : costlier ? '↑' : '='} global: {globalTok}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {canOverrideDelete && (
                        <Tooltip title="Delete override">
                          <IconButton size="small" color="error" sx={{ ml: 1 }}
                            disabled={ovDeleting === ov.id}
                            onClick={() => deleteOverride(ov.id)}>
                            {ovDeleting === ov.id
                              ? <CircularProgress size={14} />
                              : <DeleteOutline sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Add new override form */}
          {canOverrideCreate && (
            <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
              borderRadius: 2, p: 2 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                letterSpacing={0.5} textTransform="uppercase" mb={2} display="block">
                New Override
              </Typography>

              <Box display="grid" sx={{ gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Apply to *</InputLabel>
                  <Select label="Apply to *" value={ovForm.scope_type}
                    onChange={e => {
                      setOvForm(f => ({ ...f, scope_type: e.target.value, scope_target_id: '' }));
                      setSelectedTarget(null);
                      setMerchSearch('');
                    }}>
                    {Object.entries(SCOPE_LABELS).map(([v, l]) => (
                      <MenuItem key={v} value={v}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Resolution *</InputLabel>
                  <Select label="Resolution *" value={ovForm.resolution}
                    onChange={e => setOvForm(f => ({ ...f, resolution: e.target.value }))}>
                    {resolutions.map(r => (
                      <MenuItem key={r} value={r}>{RES_LABELS[r] || r.toUpperCase()}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Currency *</InputLabel>
                  <Select label="Currency *" value={ovForm.currency_code || globalCurrency}
                    onChange={e => setOvForm(f => ({ ...f, currency_code: e.target.value }))}>
                    {(matrix?.currencies || []).map(c => (
                      <MenuItem key={c.code} value={c.code}>{c.code} — {c.display_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {ovForm.scope_type === 'plan' && (
                <Autocomplete size="small" fullWidth sx={{ mb: 2 }}
                  options={planOptions}
                  getOptionLabel={p => `${p.display_name} (${p.plan_code})`}
                  value={selectedTarget}
                  onChange={(_, v) => {
                    setSelectedTarget(v);
                    setOvForm(f => ({ ...f, scope_target_id: v?.id || '' }));
                  }}
                  renderInput={params => <TextField {...params} label="Select Plan *" />}
                  noOptionsText="No plans found" />
              )}

              {ovForm.scope_type === 'merchant' && (
                <Autocomplete size="small" fullWidth sx={{ mb: 2 }}
                  options={merchantOpts}
                  getOptionLabel={u => `${u.display_name} (${u.email})`}
                  filterOptions={x => x}
                  value={selectedTarget}
                  onInputChange={(_, v) => setMerchSearch(v)}
                  onChange={(_, v) => {
                    setSelectedTarget(v);
                    setOvForm(f => ({ ...f, scope_target_id: v?.user_id || '' }));
                  }}
                  renderInput={params =>
                    <TextField {...params} label="Select Merchant *"
                      helperText="Type at least 2 characters to search" />}
                  noOptionsText={merchantSearch.length < 2 ? 'Type 2+ characters…' : 'No merchants found'} />
              )}

              {ovForm.scope_type === 'package' && (
                <TextField size="small" fullWidth label="Addon / Package ID *"
                  type="number" value={ovForm.scope_target_id}
                  error={!!ovErrors.scope_target_id}
                  helperText={ovErrors.scope_target_id || ' '}
                  onChange={e => { setOvForm(f => ({ ...f, scope_target_id: e.target.value })); if (ovErrors.scope_target_id) setOvErrors(p => ({ ...p, scope_target_id: '' })); }}
                  sx={{ ...NUM_NO_SPIN, mb: 2 }} />
              )}

              {/* Show scope_target_id error for plan/merchant (Autocomplete doesn't have helperText wiring) */}
              {ovErrors.scope_target_id && ovForm.scope_type !== 'package' && (
                <Typography variant="caption" color="error" display="block" sx={{ mb: 1, mt: -1 }}>
                  {ovErrors.scope_target_id}
                </Typography>
              )}

              <TextField size="small" fullWidth label="Token Cost *"
                type="number" value={ovForm.tokens}
                placeholder={`e.g. ${globalRatesFor(overrideCt, globalCurrency)?.[ovForm.resolution]?.tokens || 10}`}
                error={!!ovErrors.tokens}
                helperText={ovErrors.tokens || (globalRatesFor(overrideCt, globalCurrency)?.[ovForm.resolution]
                  ? `Global ${globalCurrency} is ${globalRatesFor(overrideCt, globalCurrency)[ovForm.resolution].tokens} tokens`
                  : 'Tokens to charge for this resolution')}
                onChange={e => { setOvForm(f => ({ ...f, tokens: e.target.value })); if (ovErrors.tokens) setOvErrors(p => ({ ...p, tokens: '' })); }}
                onBlur={() => {
                  if (ovForm.tokens === '') setOvErrors(p => ({ ...p, tokens: 'Token cost is required' }));
                  else if (Number(ovForm.tokens) < 0) setOvErrors(p => ({ ...p, tokens: 'Cannot be negative' }));
                  else setOvErrors(p => ({ ...p, tokens: '' }));
                }}
                inputProps={{ min: 0 }}
                sx={NUM_NO_SPIN} />
            </Box>
          )}
        </DialogContent>

        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setOverrideCt(null)} color="inherit" sx={{ fontWeight: 600 }}>
            Close
          </Button>
          {canOverrideCreate && (
            <Button variant="contained" onClick={saveOverride}
              disabled={ovSaving || ovForm.scope_target_id === '' || ovForm.tokens === ''}
              startIcon={ovSaving ? <CircularProgress size={14} color="inherit" /> : <Check />}
              sx={{ fontWeight: 700, borderRadius: 2, minWidth: 130 }}>
              {ovSaving ? 'Saving…' : 'Save Override'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
